/**
 * GanttPanel — In-panel Gantt chart view + pop-out schedule editor.
 *
 * Mode 1 (In-Panel): Read-only Gantt bar visualization with zoom controls
 *   and an "Open Editor" button.
 * Mode 2 (Pop-Out): Full-screen modal with grid editor (left) and
 *   Gantt chart (right) side-by-side.
 *
 * Epic: DEV-396, Tasks: DEV-400/DEV-401
 * PRD: docs/PRD-Gantt-Schedule-Visualization.md §5.4–5.5
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  GanttTask,
  GanttTaskResponse,
  apiResponseToGanttTask,
  fetchGanttTasksByProject,
  batchUpsertGanttTasks,
  recalculateGanttSchedule,
} from '../../api/ganttTasks';
import { ApiError } from '../../api/client';
import { fetchForKey, QUERY_KEYS, CACHE_UPDATE_EVENT } from '../../api/queryCache';
import { Group as PanelGroup, Panel, Separator as PanelSeparator } from 'react-resizable-panels';
import GanttChart from '../GanttChart/GanttChart';
import type { GanttColorTheme } from '../GanttChart/GanttChart';
import {
  parsePredecessors,
  parsePredecessorToken,
  validatePredecessors as validatePredecessorSyntax,
  renumberPredecessors,
  extractPredecessorTaskIds,
} from './predecessorParser';
import {
  fetchBaselineSummaries,
  fetchBaselineTasks as fetchBaselineTasksApi,
  lockBaseline as lockBaselineApi,
} from '../../api/ganttBaselines';
import { fetchProjectRoleSlots } from '../../api/projectRoles';
import type { ProjectRoleSlotResponse } from '../../api/projectRoles';
import {
  fetchTaskRoleAssignments,
  bulkCreateTaskRoleAssignments,
  deleteTaskRoleAssignment,
  updateTaskRoleAssignmentType,
  buildTaskRoleAssignmentMap,
} from '../../api/ganttTaskRoleAssignments';
import type {
  TaskRoleAssignmentEnriched,
  TaskRoleAssignmentMap,
  ResponsibilityType,
} from '../../api/ganttTaskRoleAssignments';
import { RoleBadgeGroup } from './RoleBadge';
import GanttRoleAssignmentTray from './GanttRoleAssignmentTray';
import styles from './GanttPanel.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface GanttPanelProps {
  projectGuid: string;
  projectName?: string;
  canEdit?: boolean;
  debugMode?: boolean;
  /** When true, open the Full View editor immediately on mount. */
  autoOpen?: boolean;
  /** Called when the editor is closed (so the parent can restore previous view). */
  onClose?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const MAX_TASKS = 100;
const WARN_THRESHOLD = 80;
const EMPTY_TASK = (): GanttTask => ({
  id: '',
  projectId: '',
  taskId: 0,
  wbs: '',
  taskName: '',
  durationDays: null,
  startDate: null,
  endDate: null,
  percentComplete: 0,
  predecessors: '',
  dependencyType: 'FS',
  indentLevel: 0,
  isMilestone: false,
  isSummary: false,
  isPinned: false,
  summaryLevel: null,
  assignedTo: null,
  notes: '',
  sortOrder: 0,
  lateStart: null,
  lateFinish: null,
  totalFloat: null,
  isCritical: false,
});

/**
 * Auto-derive start/end dates for a task from its predecessors.
 * Used when a user enters a predecessor on a dateless task so
 * the chart can show the bar immediately without manual date entry.
 *
 * Rules by dependency type (using the first valid predecessor):
 *   FS: start = predecessor.endDate + lag
 *   SS: start = predecessor.startDate + lag
 *   FF: end   = predecessor.endDate + lag  → start = end - duration
 *   SF: end   = predecessor.startDate + lag → start = end - duration
 *
 * For milestones (duration 0), start = end.
 */
function deriveDatesFromPredecessors(
  preds: import('./predecessorParser').ParsedPredecessor[],
  allTasks: GanttTask[],
  task: GanttTask
): { start: string; end: string } | null {
  for (const pred of preds) {
    const predTask = allTasks.find((t) => t.taskId === pred.taskId);
    if (!predTask) continue;

    const dur = task.durationDays ?? 0;

    const addDays = (iso: string, days: number): string => {
      const d = new Date(iso + 'T00:00:00');
      d.setDate(d.getDate() + days);
      return d.toISOString().slice(0, 10);
    };

    if (pred.type === 'FS' && predTask.endDate) {
      const start = addDays(predTask.endDate, pred.lag);
      const end = dur > 0 ? addDays(start, dur) : start;
      return { start, end };
    }
    if (pred.type === 'SS' && predTask.startDate) {
      const start = addDays(predTask.startDate, pred.lag);
      const end = dur > 0 ? addDays(start, dur) : start;
      return { start, end };
    }
    if (pred.type === 'FF' && predTask.endDate) {
      const end = addDays(predTask.endDate, pred.lag);
      const start = dur > 0 ? addDays(end, -dur) : end;
      return { start, end };
    }
    if (pred.type === 'SF' && predTask.startDate) {
      const end = addDays(predTask.startDate, pred.lag);
      const start = dur > 0 ? addDays(end, -dur) : end;
      return { start, end };
    }
  }
  return null;
}

type ViewMode = 'Day' | 'Week' | 'Month' | 'Year';

/* ------------------------------------------------------------------ */
/*  DEV-567: Column visibility definitions                             */
/* ------------------------------------------------------------------ */

/** Toggleable column keys. Task Name is NOT listed — it's always visible. */
type ColumnKey = 'taskId' | 'wbs' | 'summaryLevel' | 'duration' | 'start' | 'end' | 'predecessors' | 'pctComplete' | 'float' | 'pinned' | 'roles';

interface ColumnDef {
  key: ColumnKey;
  /** i18n key under `gantt.*` — reuses existing col header keys */
  labelKey: string;
  /** Default column width for colgroup */
  width: number | undefined;
  /** Minimum width the user can drag to */
  minWidth?: number;
  /** If true, column is only available when a specific feature toggle is on */
  featureGate?: 'criticalPath';
}

const COLUMN_DEFS: ColumnDef[] = [
  { key: 'taskId',       labelKey: 'colTaskId',       width: 44,  minWidth: 36 },
  { key: 'wbs',          labelKey: 'colWbs',          width: 70,  minWidth: 44 },
  // Task Name is NOT in this array — always visible, never toggleable
  { key: 'summaryLevel', labelKey: 'colSummaryLevel', width: 90,  minWidth: 60 },
  { key: 'duration',     labelKey: 'colDuration',     width: 65,  minWidth: 44 },
  { key: 'start',        labelKey: 'colStart',        width: 110, minWidth: 70 },
  { key: 'end',          labelKey: 'colEnd',          width: 110, minWidth: 70 },
  { key: 'predecessors', labelKey: 'colPredecessors',  width: 90,  minWidth: 50 },
  { key: 'pctComplete',  labelKey: 'colComplete',     width: 80,  minWidth: 44 },
  { key: 'float',        labelKey: 'colFloat',        width: 50,  minWidth: 36, featureGate: 'criticalPath' },
  { key: 'pinned',       labelKey: 'colPinned',       width: 44,  minWidth: 36 },
  { key: 'roles',        labelKey: 'colRoles',        width: 140, minWidth: 60 },
];

/** All column keys that are visible by default (summaryLevel hidden by default) */
const HIDDEN_BY_DEFAULT = new Set<ColumnKey>(['summaryLevel']);
const DEFAULT_VISIBLE = new Set<ColumnKey>(COLUMN_DEFS.filter((c) => !HIDDEN_BY_DEFAULT.has(c.key)).map((c) => c.key));

function loadColumnVisibility(projectGuid: string): Set<ColumnKey> {
  try {
    const raw = localStorage.getItem(`gantt-columns-${projectGuid}`);
    if (!raw) return new Set(DEFAULT_VISIBLE);
    const parsed = JSON.parse(raw) as { visible: ColumnKey[]; knownKeys: ColumnKey[] };
    const result = new Set<ColumnKey>(parsed.visible);
    // Any column in COLUMN_DEFS that wasn't in knownKeys at save time is NEW
    // Default visible unless in HIDDEN_BY_DEFAULT
    const known = new Set<ColumnKey>(parsed.knownKeys ?? parsed.visible);
    for (const col of COLUMN_DEFS) {
      if (!known.has(col.key) && !HIDDEN_BY_DEFAULT.has(col.key)) result.add(col.key);
    }
    return result;
  } catch {
    return new Set(DEFAULT_VISIBLE);
  }
}

function saveColumnVisibility(projectGuid: string, visible: Set<ColumnKey>) {
  const payload = {
    visible: [...visible],
    knownKeys: COLUMN_DEFS.map((c) => c.key),
  };
  localStorage.setItem(`gantt-columns-${projectGuid}`, JSON.stringify(payload));
}

/* ------------------------------------------------------------------ */
/*  Column width persistence (resizable columns)                       */
/* ------------------------------------------------------------------ */

/** All resizable width keys — ColumnKey plus the always-visible Task Name. */
type WidthKey = ColumnKey | 'taskName';

const TASK_NAME_DEFAULT_WIDTH = 200;
const TASK_NAME_MIN_WIDTH = 100;

type ColumnWidthMap = Partial<Record<WidthKey, number>>;

/** Build the default width map from COLUMN_DEFS + Task Name. */
function defaultColumnWidths(): ColumnWidthMap {
  const m: ColumnWidthMap = { taskName: TASK_NAME_DEFAULT_WIDTH };
  for (const col of COLUMN_DEFS) {
    if (col.width != null) m[col.key] = col.width;
  }
  return m;
}

function loadColumnWidths(projectGuid: string): ColumnWidthMap {
  try {
    const raw = localStorage.getItem(`gantt-col-widths-${projectGuid}`);
    if (!raw) return defaultColumnWidths();
    const parsed = JSON.parse(raw) as ColumnWidthMap;
    // Merge with defaults so new columns get their default width
    return { ...defaultColumnWidths(), ...parsed };
  } catch {
    return defaultColumnWidths();
  }
}

function saveColumnWidths(projectGuid: string, widths: ColumnWidthMap) {
  localStorage.setItem(`gantt-col-widths-${projectGuid}`, JSON.stringify(widths));
}

/**
 * Normalize a date string from various common formats to ISO YYYY-MM-DD.
 * Handles: "3/27/2026", "03/27/2026", "27/03/2026", "Mar 27, 2026",
 * "March 27, 2026", "27 Mar 2026", "2026-03-27", "2026/03/27".
 * Returns null if the string cannot be parsed into a valid date.
 */
function normalizeDateToISO(raw: string): string | null {
  if (!raw) return null;
  const s = raw.trim();

  // Already ISO: YYYY-MM-DD or YYYY/MM/DD
  const isoMatch = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const dt = new Date(+y, +m - 1, +d);
    if (!isNaN(dt.getTime())) {
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
  }

  // Slash/dash formats: M/D/YYYY or D/M/YYYY — use heuristic: if first
  // number > 12, it's DD/MM/YYYY; otherwise assume US M/D/YYYY.
  const slashMatch = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (slashMatch) {
    let [, a, b, y] = slashMatch;
    let month: number, day: number;
    if (+a > 12) {
      // Must be DD/MM/YYYY
      day = +a; month = +b;
    } else if (+b > 12) {
      // Must be MM/DD/YYYY
      month = +a; day = +b;
    } else {
      // Ambiguous — assume US: MM/DD/YYYY
      month = +a; day = +b;
    }
    const dt = new Date(+y, month - 1, day);
    if (!isNaN(dt.getTime()) && dt.getMonth() === month - 1) {
      return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  // Named month: "Mar 27, 2026", "March 27, 2026", "27 Mar 2026", etc.
  const MONTHS: Record<string, number> = {
    jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3,
    apr: 4, april: 4, may: 5, jun: 6, june: 6,
    jul: 7, july: 7, aug: 8, august: 8, sep: 9, september: 9,
    oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
  };
  // "Mar 27, 2026" or "March 27 2026"
  const namedA = s.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (namedA) {
    const month = MONTHS[namedA[1].toLowerCase()];
    if (month) {
      const day = +namedA[2], year = +namedA[3];
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  // "27 Mar 2026"
  const namedB = s.match(/^(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})$/);
  if (namedB) {
    const month = MONTHS[namedB[2].toLowerCase()];
    if (month) {
      const day = +namedB[1], year = +namedB[3];
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  // Last resort: try Date.parse (handles many locale-dependent formats)
  const fallback = new Date(s);
  if (!isNaN(fallback.getTime()) && fallback.getFullYear() > 1900) {
    return fallback.toISOString().slice(0, 10);
  }

  return null;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function GanttPanel({
  projectGuid,
  projectName,
  canEdit = true,
  debugMode,
  autoOpen = false,
  onClose,
}: GanttPanelProps) {
  const { t } = useAppTranslation('pages');

  // ── State ─────────────────────────────────────────────────
  const [tasks, setTasks] = useState<GanttTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(autoOpen);
  const [viewMode, setViewMode] = useState<ViewMode>('Month');
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [validated, setValidated] = useState(false);
  const [clearFlash, setClearFlash] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  // DEV-565: Critical path toggle (default off to avoid visual clutter)
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  // DEV-564: Baseline state
  const [showBaseline, setShowBaseline] = useState(false);
  const [baselineSummaries, setBaselineSummaries] = useState<import('../../api/ganttBaselines').BaselineSummary[]>([]);
  const [selectedBaselineNumber, setSelectedBaselineNumber] = useState<number | null>(null);
  const [baselineTasks, setBaselineTasks] = useState<import('../../api/ganttBaselines').BaselineTask[]>([]);

  // Role-based task assignment state
  const [roleSlots, setRoleSlots] = useState<ProjectRoleSlotResponse[]>([]);
  const [taskRoleAssignments, setTaskRoleAssignments] = useState<TaskRoleAssignmentEnriched[]>([]);
  const [taskRoleMap, setTaskRoleMap] = useState<TaskRoleAssignmentMap>(new Map());
  const [roleTrayOpen, setRoleTrayOpen] = useState(false);

  // DEV-567: Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(() => loadColumnVisibility(projectGuid));
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const columnsDropdownRef = useRef<HTMLDivElement>(null);

  // ── Column width resizing (moved above RACI toggle to avoid TDZ) ──
  const [columnWidths, setColumnWidths] = useState<ColumnWidthMap>(() => loadColumnWidths(projectGuid));
  const resizingRef = useRef<{ key: WidthKey; startX: number; startW: number } | null>(null);

  // RACI View Mode
  type GanttViewMode = 'schedule' | 'raci';
  const [ganttViewMode, setGanttViewMode] = useState<GanttViewMode>('schedule');
  const savedScheduleConfigRef = useRef<{ columns: Set<ColumnKey>; widths: ColumnWidthMap } | null>(null);

  const toggleGanttViewMode = useCallback(() => {
    if (ganttViewMode === 'schedule') {
      // Save current config before switching
      savedScheduleConfigRef.current = {
        columns: new Set(visibleColumns),
        widths: { ...columnWidths },
      };
      // Switch to RACI: only ID, WBS, Task Name (always visible), Roles
      const raciCols = new Set<ColumnKey>(['taskId', 'wbs', 'roles']);
      setVisibleColumns(raciCols);
      setColumnWidths(prev => ({ ...prev, roles: 300, taskName: 300 }));
      setGanttViewMode('raci');
    } else {
      // Restore saved config
      if (savedScheduleConfigRef.current) {
        setVisibleColumns(savedScheduleConfigRef.current.columns);
        setColumnWidths(savedScheduleConfigRef.current.widths);
      }
      setGanttViewMode('schedule');
    }
  }, [ganttViewMode, visibleColumns, columnWidths]);

  const isColVisible = useCallback((key: ColumnKey): boolean => {
    const col = COLUMN_DEFS.find((c) => c.key === key);
    // Feature-gated columns: if the gate is off, column is hidden regardless of user preference
    if (col?.featureGate === 'criticalPath' && !showCriticalPath) return false;
    return visibleColumns.has(key);
  }, [visibleColumns, showCriticalPath]);

  const showColumn = useCallback((key: ColumnKey) => {
    setVisibleColumns((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      saveColumnVisibility(projectGuid, next);
      return next;
    });
  }, [projectGuid]);

  const hideColumn = useCallback((key: ColumnKey) => {
    setVisibleColumns((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      saveColumnVisibility(projectGuid, next);
      return next;
    });
  }, [projectGuid]);

  // Close columns dropdown on outside click
  useEffect(() => {
    if (!columnsDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (columnsDropdownRef.current && !columnsDropdownRef.current.contains(e.target as Node)) {
        setColumnsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [columnsDropdownOpen]);

  /** Get the current width for any resizable column (toggleable or Task Name). */
  const getColWidth = useCallback((key: WidthKey): number => {
    if (key === 'taskName') return columnWidths.taskName ?? TASK_NAME_DEFAULT_WIDTH;
    return columnWidths[key] ?? (COLUMN_DEFS.find((c) => c.key === key)?.width ?? 80);
  }, [columnWidths]);

  /** Reset all column widths to defaults. */
  const resetColumnWidths = useCallback(() => {
    const defaults = defaultColumnWidths();
    setColumnWidths(defaults);
    saveColumnWidths(projectGuid, defaults);
  }, [projectGuid]);

  /** Look up the minimum width for a WidthKey. */
  const getMinWidth = useCallback((key: WidthKey): number => {
    if (key === 'taskName') return TASK_NAME_MIN_WIDTH;
    return COLUMN_DEFS.find((c) => c.key === key)?.minWidth ?? 30;
  }, []);

  /**
   * Start a column resize drag. Uses pointer events for reliable cross-browser
   * drag tracking (works even if the pointer leaves the table header).
   */
  const handleResizePointerDown = useCallback((e: React.PointerEvent, key: WidthKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = getColWidth(key);
    const minW = getMinWidth(key);

    // Capture pointer on the target so we get events even outside the element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    const onMove = (ev: PointerEvent) => {
      const delta = ev.clientX - startX;
      const newW = Math.max(minW, startW + delta);
      setColumnWidths((prev) => ({ ...prev, [key]: newW }));
    };

    const onUp = (ev: PointerEvent) => {
      (ev.target as HTMLElement).releasePointerCapture(ev.pointerId);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      // Persist final width
      setColumnWidths((prev) => {
        const final = { ...prev };
        saveColumnWidths(projectGuid, final);
        return final;
      });
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [getColWidth, getMinWidth, projectGuid]);

  /**
   * Double-click on a resize handle resets that column to its default width.
   */
  const handleResizeDoubleClick = useCallback((key: WidthKey) => {
    if (key === 'taskName') {
      setColumnWidths((prev) => {
        const next = { ...prev, taskName: TASK_NAME_DEFAULT_WIDTH };
        saveColumnWidths(projectGuid, next);
        return next;
      });
      return;
    }
    const def = COLUMN_DEFS.find((c) => c.key === key);
    if (!def?.width) return;
    setColumnWidths((prev) => {
      const next = { ...prev, [key]: def.width! };
      saveColumnWidths(projectGuid, next);
      return next;
    });
  }, [projectGuid]);

  // ── Search / highlight state (hooks declared here; derived values after draftTasks) ──
  const [searchTerm, setSearchTerm] = useState('');
  const [activeMatchIdx, setActiveMatchIdx] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Collapsed summary rows — tracks which taskIds are collapsed
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());

  // Draft state for editor (separate from saved state)
  const [draftTasks, setDraftTasks] = useState<GanttTask[]>([]);

  // ── Search derived values (must be after draftTasks declaration) ──

  /** Indices into draftTasks that match the current search term (task name, WBS, or task ID). */
  const searchMatchIndices = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [] as number[];
    const matches: number[] = [];
    for (let i = 0; i < draftTasks.length; i++) {
      const task = draftTasks[i];
      if (
        task.taskName.toLowerCase().includes(q) ||
        task.wbs.toLowerCase().includes(q) ||
        String(task.taskId) === q
      ) {
        matches.push(i);
      }
    }
    return matches;
  }, [draftTasks, searchTerm]);

  /** Set of matching indices for O(1) lookup during row rendering. */
  const searchMatchSet = useMemo(() => new Set(searchMatchIndices), [searchMatchIndices]);

  /** The draftTasks index of the currently focused match. */
  const currentMatchDraftIdx = searchMatchIndices.length > 0
    ? searchMatchIndices[Math.min(activeMatchIdx, searchMatchIndices.length - 1)]
    : -1;

  // Clamp activeMatchIdx when matches change
  useEffect(() => {
    if (searchMatchIndices.length === 0) {
      setActiveMatchIdx(0);
    } else if (activeMatchIdx >= searchMatchIndices.length) {
      setActiveMatchIdx(searchMatchIndices.length - 1);
    }
  }, [searchMatchIndices.length, activeMatchIdx]);

  const navigateSearch = useCallback((direction: 1 | -1) => {
    if (searchMatchIndices.length === 0) return;
    setActiveMatchIdx((prev) => {
      const next = prev + direction;
      if (next < 0) return searchMatchIndices.length - 1;
      if (next >= searchMatchIndices.length) return 0;
      return next;
    });
  }, [searchMatchIndices.length]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setActiveMatchIdx(0);
  }, []);

  // Ctrl+F / Cmd+F focuses the search input when the editor is open
  useEffect(() => {
    if (!editorOpen) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [editorOpen]);

  // Validation popup state
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationTitle, setValidationTitle] = useState<string>('');

  const mountedRef = useRef(true);

  // Scroll-sync refs for grid ↔ chart panes
  const gridPaneRef = useRef<HTMLDivElement>(null);
  const chartPaneRef = useRef<HTMLDivElement>(null);
  const isSyncingScroll = useRef(false);
  const cleanupScrollSync = useRef<(() => void) | null>(null);

  // ── Gantt color theme ───────────────────────────────────────
  // Always use white background for the editor — readability over aesthetics.
  // The in-panel read-only preview could use match_theme in the future.
  const colorTheme: GanttColorTheme = 'white';

  // ── Data Loading ──────────────────────────────────────────
  const loadTasks = useCallback(async (force = false) => {
    try {
      setLoading(true);
      const cacheKey = `${QUERY_KEYS.gantt}_${projectGuid}`;
      const raw = await fetchForKey<GanttTaskResponse[]>(
        cacheKey,
        () => fetchGanttTasksByProject(projectGuid),
        force ? { force: true } : undefined
      );
      if (mountedRef.current) {
        const mapped = raw.map((r, i) => apiResponseToGanttTask(r, i));
        setTasks(mapped);
        setDraftTasks(mapped);
        setError(null);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setError(err.message || 'Failed to load Gantt tasks');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [projectGuid]);

  useEffect(() => {
    mountedRef.current = true;
    if (projectGuid) loadTasks();

    const handleCacheUpdate = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key === `${QUERY_KEYS.gantt}_${projectGuid}`) {
        loadTasks();
      }
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    };
  }, [projectGuid, loadTasks]);

  // ── Role-based assignment data loading ──────────────────────
  const loadRoleData = useCallback(async () => {
    if (!projectGuid) return;
    try {
      const [slots, assignments] = await Promise.all([
        fetchProjectRoleSlots(projectGuid),
        fetchTaskRoleAssignments(projectGuid),
      ]);
      if (mountedRef.current) {
        setRoleSlots(slots);
        setTaskRoleAssignments(assignments);
        setTaskRoleMap(buildTaskRoleAssignmentMap(assignments));
      }
    } catch {
      // Non-fatal: role data is supplementary; Gantt works without it
      if (mountedRef.current) {
        setRoleSlots([]);
        setTaskRoleAssignments([]);
        setTaskRoleMap(new Map());
      }
    }
  }, [projectGuid]);

  useEffect(() => {
    loadRoleData();
  }, [loadRoleData]);

  // ── Role assignment handlers ──────────────────────────────────
  const handleBulkAssignRole = useCallback(async (roleSlotGuid: string, taskIds: string[]) => {
    if (taskIds.length === 0) return;
    try {
      await bulkCreateTaskRoleAssignments(projectGuid, {
        assignments: taskIds.map((ganttTaskId) => ({
          ganttTaskId,
          roleSlotGuid,
          responsibilityType: 'responsible' as const,
        })),
      });
      // Reload assignment data
      await loadRoleData();
      setStatusMsg(t('gantt.rolesUpdated') || 'Roles updated');
      setTimeout(() => setStatusMsg(null), 2000);
    } catch (err: any) {
      setStatusMsg(err.message || 'Failed to assign role');
      setTimeout(() => setStatusMsg(null), 3000);
    }
  }, [projectGuid, loadRoleData, t]);

  const handleRemoveRoleAssignment = useCallback(async (assignmentId: string) => {
    try {
      await deleteTaskRoleAssignment(projectGuid, assignmentId);
      await loadRoleData();
    } catch (err: any) {
      setStatusMsg(err.message || 'Failed to remove assignment');
      setTimeout(() => setStatusMsg(null), 3000);
    }
  }, [projectGuid, loadRoleData]);

  const handleChangeRoleType = useCallback(async (assignmentId: string, newType: ResponsibilityType) => {
    try {
      await updateTaskRoleAssignmentType(projectGuid, assignmentId, newType);
      await loadRoleData();
    } catch (err: any) {
      setStatusMsg(err.message || 'Failed to update role type');
      setTimeout(() => setStatusMsg(null), 3000);
    }
  }, [projectGuid, loadRoleData]);

  // ── DEV-564: Load baseline summaries ──────────────────────
  const loadBaselineSummaries = useCallback(async () => {
    try {
      const summaries = await fetchBaselineSummaries(projectGuid);
      setBaselineSummaries(summaries);
      // Auto-select most recent baseline if none selected
      if (summaries.length > 0 && selectedBaselineNumber === null) {
        setSelectedBaselineNumber(summaries[0].baselineNumber);
      }
    } catch {
      // Baselines are non-critical — fail silently
    }
  }, [projectGuid, selectedBaselineNumber]);

  useEffect(() => {
    if (projectGuid) loadBaselineSummaries();
  }, [projectGuid, loadBaselineSummaries]);

  // Fetch baseline task data when selection changes or toggle is on
  useEffect(() => {
    if (!showBaseline || selectedBaselineNumber === null) {
      setBaselineTasks([]);
      return;
    }
    let cancelled = false;
    fetchBaselineTasksApi(projectGuid, selectedBaselineNumber)
      .then((tasks) => { if (!cancelled) setBaselineTasks(tasks); })
      .catch(() => { if (!cancelled) setBaselineTasks([]); });
    return () => { cancelled = true; };
  }, [projectGuid, selectedBaselineNumber, showBaseline]);

  // ── DEV-564: Lock Baseline ──────────────────────────────
  const handleLockBaseline = useCallback(async () => {
    const taskCount = tasks.filter((t) => !t.isSummary).length;
    if (taskCount === 0) return;
    const confirmed = window.confirm(
      t('gantt.lockBaselineConfirm', { count: taskCount })
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      const result = await lockBaselineApi(projectGuid);
      setStatusMsg(t('gantt.lockSuccess', { number: result.baselineNumber }));
      // Refresh baseline list and auto-select the new one
      await loadBaselineSummaries();
      setSelectedBaselineNumber(result.baselineNumber);
    } catch (err: any) {
      setStatusMsg(err.message || 'Failed to lock baseline');
    } finally {
      setSaving(false);
    }
  }, [projectGuid, tasks, t, loadBaselineSummaries]);

  // ── Scroll Sync: grid ↔ Frappe .gantt-container vertical scroll ──
  // Frappe Gantt's .gantt-container handles both axes (overflow: auto).
  // We sync .gridPane ↔ .gantt-container vertical scrollTop so the
  // left-side grid rows stay aligned with the right-side Gantt bars.
  //
  // Challenge: Frappe Gantt may recreate its internal DOM on refresh(),
  // and the GanttChart React component may mount after GanttPanel's
  // effect fires. We use a triple approach:
  //   1. Initial delayed attach (300ms for first render)
  //   2. MutationObserver for DOM changes (Frappe re-renders)
  //   3. Periodic polling (500ms) as a safety net
  const lastGanttContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!editorOpen) return;

    // Refs for cleanup across the entire effect lifecycle
    let initTimer: ReturnType<typeof setTimeout>;
    let pollId: ReturnType<typeof setInterval>;
    let observer: MutationObserver | null = null;

    /**
     * Attach scroll sync listeners between a grid element and a .gantt-container.
     * Returns a cleanup function.
     */
    const attachScrollSync = (gridEl: HTMLElement, ganttContainer: HTMLElement): (() => void) => {
      let activeScroller: HTMLElement | null = null;
      let guardTimer = 0;
      let rafId = 0;

      const makeHandler = (source: HTMLElement, target: HTMLElement, isGridToGantt: boolean) => () => {
        if (activeScroller && activeScroller !== source) return;
        activeScroller = source;
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          // Apply header height offset: when syncing grid→gantt, the grid
          // needs a small scroll offset to compensate for header height diff.
          const offset = parseInt(gridEl.dataset.scrollOffset || '0', 10);
          if (isGridToGantt) {
            target.scrollTop = source.scrollTop + offset;
          } else {
            target.scrollTop = source.scrollTop - offset;
          }
        });
        window.clearTimeout(guardTimer);
        guardTimer = window.setTimeout(() => {
          activeScroller = null;
        }, 50);
      };

      const gridHandler = makeHandler(gridEl, ganttContainer, true);
      const ganttHandler = makeHandler(ganttContainer, gridEl, false);

      gridEl.addEventListener('scroll', gridHandler, { passive: true });
      ganttContainer.addEventListener('scroll', ganttHandler, { passive: true });

      return () => {
        cancelAnimationFrame(rafId);
        window.clearTimeout(guardTimer);
        gridEl.removeEventListener('scroll', gridHandler);
        ganttContainer.removeEventListener('scroll', ganttHandler);
      };
    };

    /**
     * Find the current .gantt-container and (re-)attach sync if needed.
     * Reads refs fresh each time in case the portal hasn't mounted yet.
     */
    const tryAttach = (): boolean => {
      const gridEl = gridPaneRef.current;
      const chartEl = chartPaneRef.current;
      if (!gridEl || !chartEl) return false;

      const ganttContainer = chartEl.querySelector('.gantt-container') as HTMLElement | null;
      if (!ganttContainer) return false;

      // Already tracking this exact element — nothing to do
      if (ganttContainer === lastGanttContainerRef.current) return true;

      // New or first element — detach old listeners, attach new ones
      cleanupScrollSync.current?.();
      lastGanttContainerRef.current = ganttContainer;
      cleanupScrollSync.current = attachScrollSync(gridEl, ganttContainer);

      // ── Row alignment: measure Frappe header vs grid thead height
      // difference. Store as data attribute for scroll sync offset.
      requestAnimationFrame(() => {
        const frappeHeader = ganttContainer.querySelector('.grid-header') as SVGGElement | null;
        const gridThead = gridEl.querySelector('thead') as HTMLElement | null;
        if (frappeHeader && gridThead) {
          const frappeHeaderH = frappeHeader.getBoundingClientRect().height;
          const gridTheadH = gridThead.getBoundingClientRect().height;
          const diff = Math.round(frappeHeaderH - gridTheadH);
          gridEl.dataset.scrollOffset = String(diff);
        }
      });

      return true;
    };

    // 1. Initial attach (with delay for portal + Frappe to render)
    initTimer = setTimeout(tryAttach, 400);

    // 2. Polling — handles late portal mount, late Frappe render, and
    //    DOM replacement after refresh(). Once attached, the poll is
    //    a cheap querySelector + reference comparison (~0.01ms).
    pollId = setInterval(tryAttach, 500);

    // 3. MutationObserver for immediate re-attach on DOM changes.
    //    Deferred slightly — chartPaneRef may not be populated yet.
    setTimeout(() => {
      const chartEl = chartPaneRef.current;
      if (chartEl) {
        observer = new MutationObserver(() => {
          requestAnimationFrame(tryAttach);
        });
        observer.observe(chartEl, { childList: true, subtree: true });
      }
    }, 100);

    return () => {
      clearTimeout(initTimer);
      clearInterval(pollId);
      observer?.disconnect();
      cleanupScrollSync.current?.();
      cleanupScrollSync.current = null;
      lastGanttContainerRef.current = null;
    };
  }, [editorOpen]);

  // ── Auto-open: seed draft tasks once data finishes loading ──
  const autoOpenSeeded = useRef(false);
  useEffect(() => {
    if (autoOpen && !autoOpenSeeded.current && !loading && tasks.length >= 0) {
      autoOpenSeeded.current = true;
      setDraftTasks([...tasks]);
      setDirty(false);
    }
  }, [autoOpen, loading, tasks]);

  // ── Row selection handler (supports shift-click and ctrl/cmd-click) ──
  const lastClickedIndex = useRef<number | null>(null);
  const handleRowClick = useCallback(
    (idx: number, e: React.MouseEvent) => {
      const task = draftTasks[idx];
      if (!task) return;

      if (e.shiftKey && lastClickedIndex.current !== null) {
        // Range select from last clicked to current
        const from = Math.min(lastClickedIndex.current, idx);
        const to = Math.max(lastClickedIndex.current, idx);
        const range = new Set<number>();
        for (let i = from; i <= to; i++) range.add(i);
        setSelectedIndices(range);
      } else if (e.ctrlKey || e.metaKey) {
        // Toggle individual selection
        setSelectedIndices((prev) => {
          const next = new Set(prev);
          if (next.has(idx)) next.delete(idx);
          else next.add(idx);
          return next;
        });
      } else {
        // Single select
        setSelectedIndices(new Set([idx]));
      }
      setSelectedTaskId(task.taskId);
      lastClickedIndex.current = idx;
    },
    [draftTasks]
  );

  // ── Helpers: renumber taskIds + update predecessor refs ──
  /**
   * Renumber all taskIds sequentially (1, 2, 3…) and update
   * any predecessor references that point to changed taskIds.
   * Only renumbers when all current taskIds are simple integers.
   */
  const renumberTasks = useCallback((tasks: GanttTask[]): GanttTask[] => {
    // Build old→new taskId map
    const idMap = new Map<number, number>();
    tasks.forEach((t, i) => {
      const newId = i + 1;
      if (t.taskId !== newId) idMap.set(t.taskId, newId);
    });

    return tasks.map((t, i) => {
      const newId = i + 1;
      // Update predecessor references (preserves dependency type + lag)
      const newPreds = renumberPredecessors(t.predecessors, idMap);
      return { ...t, taskId: newId, sortOrder: i, predecessors: newPreds };
    });
  }, []);

  // ── Mark Dirty (resets validated state) ──────────────────
  const markDirty = useCallback(() => {
    setDirty(true);
    setValidated(false);
  }, []);

  // ── Editor Handlers ───────────────────────────────────────

  const openEditor = useCallback(() => {
    setDraftTasks([...tasks]);
    setDirty(false);
    setEditorOpen(true);
  }, [tasks]);

  const closeEditor = useCallback(() => {
    if (dirty && !window.confirm(t('gantt.unsavedChanges'))) return;
    setEditorOpen(false);
    setDirty(false);
    setStatusMsg(null);
    onClose?.();
  }, [dirty, t, onClose]);

  // Update a single field in a draft task
  const updateDraftField = useCallback(
    (index: number, field: keyof GanttTask, value: any) => {
      setDraftTasks((prev) => {
        const next = [...prev];
        const updated = { ...next[index], [field]: value };
        // Auto-pin when user manually sets a start date so recalculate
        // preserves the override instead of resetting it
        if (field === 'startDate' && value) {
          updated.isPinned = true;
        }
        // DEV-588: Auto-detect milestones — duration 0 ↔ milestone
        if (field === 'durationDays') {
          const dur = typeof value === 'number' ? value : parseInt(value, 10);
          if (dur === 0) {
            updated.isMilestone = true;
            // Milestones are zero-duration: collapse end to start
            if (updated.startDate) updated.endDate = updated.startDate;
          } else if (updated.isMilestone) {
            // Changing away from 0 days — clear milestone flag
            updated.isMilestone = false;
          }
        }
        // Auto-derive dates from predecessors when the task has no dates.
        // This lets users add milestones / tasks with just a predecessor
        // reference and have the chart show the bar immediately.
        if (field === 'predecessors' && value) {
          const preds = parsePredecessors(value as string);
          if (preds.length > 0 && !updated.startDate && !updated.endDate) {
            const derived = deriveDatesFromPredecessors(preds, next, updated);
            if (derived) {
              updated.startDate = derived.start;
              updated.endDate = derived.end;
            } else {
              console.warn('[auto-derive] deriveDatesFromPredecessors returned null. preds:', preds,
                'predecessor tasks:', preds.map(p => {
                  const pt = next.find(t => t.taskId === p.taskId);
                  return pt ? { taskId: pt.taskId, startDate: pt.startDate, endDate: pt.endDate, isMilestone: pt.isMilestone } : `taskId ${p.taskId} NOT FOUND`;
                }),
                'current task:', { taskId: updated.taskId, durationDays: updated.durationDays, isMilestone: updated.isMilestone }
              );
            }
          } else if (preds.length > 0) {
            console.info('[auto-derive] skipped: task already has dates. startDate:', updated.startDate, 'endDate:', updated.endDate);
          }
        }
        // Also auto-derive when duration changes and task has a predecessor but no dates.
        // Handles: user enters predecessor first (dates derived), then changes duration.
        if (field === 'durationDays' && updated.predecessors && !updated.startDate && !updated.endDate) {
          const preds = parsePredecessors(updated.predecessors);
          if (preds.length > 0) {
            const derived = deriveDatesFromPredecessors(preds, next, updated);
            if (derived) {
              updated.startDate = derived.start;
              updated.endDate = derived.end;
            }
          }
        }
        next[index] = updated;
        return next;
      });
      markDirty();
    },
    []
  );

  // ── Hierarchy Helpers ────────────────────────────────────
  // Returns indices of all descendants (children, grandchildren, etc.) of task at parentIdx
  const getDescendants = useCallback((parentIdx: number, taskList: GanttTask[]): number[] => {
    const parentLevel = taskList[parentIdx].indentLevel;
    const desc: number[] = [];
    for (let i = parentIdx + 1; i < taskList.length; i++) {
      if (taskList[i].indentLevel <= parentLevel) break;
      desc.push(i);
    }
    return desc;
  }, []);

  // Recompute isSummary flags for all tasks based on actual hierarchy
  const recomputeSummaryFlags = useCallback((taskList: GanttTask[]): GanttTask[] => {
    return taskList.map((task, idx) => {
      // A task is a summary if the next task has a deeper indent level
      const nextTask = taskList[idx + 1];
      const shouldBeSummary = nextTask ? nextTask.indentLevel > task.indentLevel : false;
      if (task.isSummary !== shouldBeSummary) {
        return { ...task, isSummary: shouldBeSummary };
      }
      return task;
    });
  }, []);

  // Add new row at end
  const addRow = useCallback(() => {
    if (draftTasks.length >= MAX_TASKS) return;
    const nextTaskId = draftTasks.length + 1;
    const nextSort = draftTasks.length;
    setDraftTasks((prev) => [
      ...prev,
      { ...EMPTY_TASK(), projectId: projectGuid, taskId: nextTaskId, sortOrder: nextSort },
    ]);
    markDirty();
  }, [draftTasks, projectGuid, markDirty]);

  // Insert row after the given index — renumber taskIds and update predecessors
  const insertAfter = useCallback((index: number) => {
    if (draftTasks.length >= MAX_TASKS) return;
    const inheritLevel = draftTasks[index]?.indentLevel ?? 0;
    const newTask: GanttTask = {
      ...EMPTY_TASK(),
      projectId: projectGuid,
      taskId: 0, // placeholder — renumberTasks will assign
      sortOrder: 0,
      indentLevel: inheritLevel,
    };
    setDraftTasks((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, newTask);
      return recomputeSummaryFlags(renumberTasks(next));
    });
    markDirty();
  }, [draftTasks, projectGuid, markDirty, renumberTasks, recomputeSummaryFlags]);

  // Delete row — renumber taskIds, update predecessors, recompute summaries
  const deleteRow = useCallback((index: number) => {
    setDraftTasks((prev) => recomputeSummaryFlags(renumberTasks(prev.filter((_, i) => i !== index))));
    markDirty();
  }, [markDirty, renumberTasks, recomputeSummaryFlags]);

  // Indent — MS Project rules: move entire subtree, enforce valid parent
  const indentRows = useCallback((indices: Set<number>) => {
    if (indices.size === 0) return;
    setDraftTasks((prev) => {
      const next = [...prev];
      // Process top-level selected rows in order; skip descendants that
      // will be moved implicitly with their parent.
      const sorted = [...indices].sort((a, b) => a - b);
      const processed = new Set<number>();

      for (const index of sorted) {
        if (processed.has(index)) continue;

        // Rule: can't indent the very first task
        if (index === 0) continue;

        const currentLevel = next[index].indentLevel;
        const aboveLevel = next[index - 1].indentLevel;

        // Rule: can only indent if it would become a child of the row above.
        // A task can't skip levels — it can indent at most to aboveLevel + 1.
        if (currentLevel >= aboveLevel + 1) continue;

        // Collect the subtree (this task + all its descendants)
        const descendants = getDescendants(index, next);
        const subtreeIndices = [index, ...descendants];

        // Indent the entire subtree by 1
        for (const si of subtreeIndices) {
          next[si] = { ...next[si], indentLevel: next[si].indentLevel + 1 };
          processed.add(si);
        }
      }

      return recomputeSummaryFlags(next);
    });
    markDirty();
  }, [markDirty, getDescendants, recomputeSummaryFlags]);

  // Outdent — MS Project rules: move entire subtree up one level, children stay attached
  const outdentRows = useCallback((indices: Set<number>) => {
    if (indices.size === 0) return;
    setDraftTasks((prev) => {
      const next = [...prev];
      const sorted = [...indices].sort((a, b) => a - b);
      const processed = new Set<number>();

      for (const index of sorted) {
        if (processed.has(index)) continue;

        // Rule: can't outdent if already at top level
        if (next[index].indentLevel <= 0) continue;

        // Collect the subtree (this task + all its descendants)
        const descendants = getDescendants(index, next);
        const subtreeIndices = [index, ...descendants];

        // Outdent the entire subtree by 1
        for (const si of subtreeIndices) {
          next[si] = { ...next[si], indentLevel: next[si].indentLevel - 1 };
          processed.add(si);
        }
      }

      return recomputeSummaryFlags(next);
    });
    markDirty();
  }, [markDirty, getDescendants, recomputeSummaryFlags]);

  // ── Collapse / Expand ────────────────────────────────────
  // Returns the range of child indices under a summary row (exclusive of the parent)
  const getChildRange = useCallback((parentIdx: number, taskList: GanttTask[]): number[] => {
    const parentLevel = taskList[parentIdx].indentLevel;
    const children: number[] = [];
    for (let i = parentIdx + 1; i < taskList.length; i++) {
      if (taskList[i].indentLevel <= parentLevel) break;
      children.push(i);
    }
    return children;
  }, []);

  const expandTask = useCallback((taskId: number) => {
    setCollapsedIds((prev) => {
      if (!prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  }, []);

  const collapseTask = useCallback((taskId: number) => {
    setCollapsedIds((prev) => {
      if (prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.add(taskId);
      return next;
    });
  }, []);

  // Compute which rows are visible (not hidden by a collapsed ancestor)
  const visibleIndices = useMemo(() => {
    const visible: number[] = [];
    let skipUntilLevel = -1;
    for (let i = 0; i < draftTasks.length; i++) {
      const task = draftTasks[i];
      // If we're skipping children of a collapsed parent
      if (skipUntilLevel >= 0) {
        if (task.indentLevel > skipUntilLevel) continue; // still inside collapsed group
        skipUntilLevel = -1; // left the collapsed group
      }
      visible.push(i);
      // If this summary row is collapsed, skip its children
      if (task.isSummary && collapsedIds.has(task.taskId)) {
        skipUntilLevel = task.indentLevel;
      }
    }
    return visible;
  }, [draftTasks, collapsedIds]);

  // The actual task objects that are visible (not hidden by collapse).
  // Passed to <GanttChart> so bar count matches grid row count.
  const visibleDraftTasks = useMemo(
    () => visibleIndices.map((i) => draftTasks[i]),
    [visibleIndices, draftTasks]
  );

  // Auto-scroll the current search match into view in the grid
  useEffect(() => {
    if (currentMatchDraftIdx < 0 || !gridPaneRef.current) return;
    // Find the <tr> in the grid that corresponds to this draft index
    const rows = gridPaneRef.current.querySelectorAll('tbody tr');
    // visibleIndices maps grid-row-position → draftTasks index.
    // Find which visible row position corresponds to currentMatchDraftIdx.
    const visiblePos = visibleIndices.indexOf(currentMatchDraftIdx);
    if (visiblePos < 0 || visiblePos >= rows.length) return;
    rows[visiblePos]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [currentMatchDraftIdx, visibleIndices]);

  // Duration-weighted % complete rollup for summary rows (client-side)
  const rollupPercentComplete = useCallback((taskList: GanttTask[]): GanttTask[] => {
    const result = [...taskList];
    // Process from deepest indent level upward so nested summaries roll up correctly
    const maxIndent = Math.max(...result.map((t) => t.indentLevel), 0);
    for (let level = maxIndent; level >= 0; level--) {
      for (let i = 0; i < result.length; i++) {
        const task = result[i];
        if (!task.isSummary || task.indentLevel !== level) continue;
        // Find direct children (indent_level + 1 that appear before next same-or-lower level)
        const children: GanttTask[] = [];
        for (let j = i + 1; j < result.length; j++) {
          if (result[j].indentLevel <= task.indentLevel) break;
          if (result[j].indentLevel === task.indentLevel + 1) children.push(result[j]);
        }
        if (children.length === 0) continue;
        // Weight each child by its duration. Milestones (duration 0 or null)
        // get a weight of 1 so they contribute equally to the rollup
        // rather than being ignored.
        const getWeight = (c: GanttTask) => (c.durationDays != null && c.durationDays > 0) ? c.durationDays : 1;
        const totalWeight = children.reduce((sum, c) => sum + getWeight(c), 0);
        if (totalWeight === 0) continue;
        const weightedPct = children.reduce(
          (sum, c) => sum + (c.percentComplete * getWeight(c)),
          0
        ) / totalWeight;
        result[i] = { ...result[i], percentComplete: Math.round(weightedPct * 10) / 10 };
      }
    }
    return result;
  }, []);

  // ── Drag & Drop ──────────────────────────────────────────
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (dragIndex === null || dragIndex === toIndex) {
      setDragIndex(null);
      return;
    }
    setDraftTasks((prev) => {
      const next = [...prev];
      // If dragging a summary row, move it and all its descendants as a group
      const descendants = getDescendants(dragIndex, next);
      const groupSize = 1 + descendants.length;
      const group = next.splice(dragIndex, groupSize);
      const insertAt = toIndex > dragIndex ? toIndex - groupSize : toIndex;
      next.splice(Math.max(0, insertAt), 0, ...group);
      return recomputeSummaryFlags(renumberTasks(next));
    });
    markDirty();
    setDragIndex(null);
  }, [dragIndex, markDirty, renumberTasks, getDescendants, recomputeSummaryFlags]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  // ── Paste from Excel ──────────────────────────────────────
  //
  // Supports two column layouts (auto-detected once across the entire paste):
  //   Layout A (with WBS): WBS | TaskName | Duration | Start | End | Predecessors | %Complete
  //   Layout B (no WBS):   TaskName | Duration | Start | End | Predecessors | %Complete
  //
  // Detection strategy: scan all rows. If the first column across the paste
  // consistently looks like a WBS code (short, no spaces, alphanumeric with
  // dots/hyphens) and never looks like a task name (no rows where col[0]
  // contains spaces), the paste has a leading WBS column.
  //
  // Summary/parent rows may have empty durations — this is expected for
  // hierarchical schedules. indentLevel is derived from WBS depth when
  // present (e.g., "L4E.1" = level 0, "L4E.1.1" = level 1).

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const text = e.clipboardData.getData('text/plain');
      if (!text || !text.includes('\t')) return; // not TSV

      e.preventDefault();
      const rows = text.split('\n').filter((r) => r.trim());
      if (rows.length === 0) return;

      // ── Phase 1: Detect column layout across the entire paste ──
      // Check if the first column is a WBS code column or a task name column.
      // A WBS column has short codes with no spaces; a task name column has
      // human-readable text (often with spaces, longer strings).
      const WBS_PATTERN = /^[A-Za-z0-9][A-Za-z0-9.\-_]*$/;
      let wbsVotes = 0;
      let nameVotes = 0;

      // Also detect header row: if the first row contains recognizable column
      // headers (case-insensitive), skip it.
      const firstCols = rows[0].split('\t').map((c) => c.trim().toLowerCase());
      const HEADER_KEYWORDS = ['wbs', 'task', 'name', 'duration', 'start', 'finish', 'end', 'predecessor', '%', 'complete'];
      const headerMatches = firstCols.filter((c) => HEADER_KEYWORDS.some((kw) => c.includes(kw))).length;
      const hasHeaderRow = headerMatches >= 3;
      const dataRows = hasHeaderRow ? rows.slice(1) : rows;

      for (const row of dataRows) {
        const col0 = row.split('\t')[0]?.trim() || '';
        if (!col0) continue;
        if (col0.includes(' ') || col0.length > 30) {
          nameVotes++;
        } else if (WBS_PATTERN.test(col0) && col0.length < 20) {
          wbsVotes++;
        } else {
          nameVotes++; // ambiguous → assume task name
        }
      }

      // WBS column detected if majority of rows have WBS-like first column
      const hasLeadingWbs = wbsVotes > 0 && wbsVotes >= nameVotes;

      // ── Phase 2: Determine minimum WBS depth for indent base ──
      // e.g., if all WBS codes start at "L4E.1", the base depth is 2 segments,
      // so "L4E.1" = indent 0, "L4E.1.1" = indent 1.
      let minWbsDepth = Infinity;
      if (hasLeadingWbs) {
        for (const row of dataRows) {
          const wbs = row.split('\t')[0]?.trim() || '';
          if (wbs) {
            const depth = wbs.split('.').length;
            if (depth < minWbsDepth) minWbsDepth = depth;
          }
        }
        if (minWbsDepth === Infinity) minWbsDepth = 1;
      }

      // ── Phase 3: Parse rows ──
      const newTasks: GanttTask[] = [];
      let nextId = draftTasks.length > 0
        ? Math.max(...draftTasks.map((t) => t.taskId)) + 1
        : 1;

      for (const row of dataRows) {
        if (draftTasks.length + newTasks.length >= MAX_TASKS) break;
        const cols = row.split('\t');

        let wbs = '';
        let indentLevel = 0;
        const offset = hasLeadingWbs ? 1 : 0;

        if (hasLeadingWbs) {
          wbs = cols[0]?.trim() || '';
          // Derive indent level from WBS segment depth relative to minimum
          const depth = wbs ? wbs.split('.').length : minWbsDepth;
          indentLevel = Math.max(0, depth - minWbsDepth);
        }

        // Column mapping (after offset): TaskName | Duration | Start | End | Predecessors | %Complete
        // If no leading WBS, check for trailing WBS column
        const trailingWbs = !hasLeadingWbs ? (cols[6]?.trim() || '') : '';
        const taskName = cols[offset + 0]?.trim() || '';
        const durationRaw = cols[offset + 1]?.trim() || '';
        const durationDays = durationRaw ? parseInt(durationRaw, 10) || null : null;

        // Skip rows with no task name (empty rows, separators)
        if (!taskName) continue;

        // DEV-588: Detect milestones — duration explicitly 0
        const isMilestone = durationDays === 0;
        const parsedStart = normalizeDateToISO(cols[offset + 2]?.trim() || '');
        const parsedEnd = normalizeDateToISO(cols[offset + 3]?.trim() || '');

        newTasks.push({
          ...EMPTY_TASK(),
          projectId: projectGuid,
          taskId: nextId++,
          wbs: wbs || trailingWbs,
          taskName,
          durationDays,
          startDate: parsedStart,
          endDate: isMilestone && parsedStart ? parsedStart : parsedEnd,
          predecessors: cols[offset + 4]?.trim() || '',
          percentComplete: cols[offset + 5] ? parseFloat(cols[offset + 5]) || 0 : 0,
          indentLevel,
          isMilestone,
          sortOrder: draftTasks.length + newTasks.length,
        });
      }

      if (newTasks.length > 0) {
        // recomputeSummaryFlags determines which rows are summary (parent) rows
        // based on the indent levels we derived from WBS depth.
        setDraftTasks((prev) => recomputeSummaryFlags(renumberTasks([...prev, ...newTasks])));
        markDirty();
      }
    },
    [draftTasks, projectGuid, renumberTasks, recomputeSummaryFlags]
  );

  // ── Client-side Validation ───────────────────────────────
  const validateTasks = useCallback((tasksToValidate: GanttTask[]): string[] => {
    const errors: string[] = [];

    if (tasksToValidate.length === 0) {
      errors.push(t('gantt.validationNoTasks'));
      return errors;
    }

    if (tasksToValidate.length > MAX_TASKS) {
      errors.push(t('gantt.validationMaxTasks', { count: tasksToValidate.length, max: MAX_TASKS }));
    }

    // Per-row checks
    const wbsMap = new Map<string, number[]>(); // wbs → row indices (1-based)
    tasksToValidate.forEach((task, idx) => {
      const rowNum = idx + 1;

      // WBS is optional — but if provided, track for duplicate check
      if (task.wbs && task.wbs.trim()) {
        const key = task.wbs.trim();
        const existing = wbsMap.get(key) || [];
        existing.push(rowNum);
        wbsMap.set(key, existing);
      }

      if (!task.taskName || !task.taskName.trim()) {
        errors.push(t('gantt.validationEmptyName', { row: rowNum }));
      }

      if (task.startDate && task.endDate && task.startDate > task.endDate) {
        errors.push(t('gantt.validationEndBeforeStart', { row: rowNum, wbs: task.wbs || String(task.taskId) }));
      }

      // Validate predecessor references — supports extended format: {taskId}[FS|SS|FF|SF][±lag]
      if (task.predecessors) {
        const validTaskIdSet = new Set(tasksToValidate.map((t2) => t2.taskId));
        const predErrors = validatePredecessorSyntax(task.predecessors, validTaskIdSet, task.taskId);
        for (const msg of predErrors) {
          errors.push(t('gantt.validationPredecessorError', { row: rowNum, detail: msg }));
        }
      }
    });

    // Duplicate WBS check (only among rows that have WBS)
    for (const [wbs, rows] of wbsMap) {
      if (rows.length > 1) {
        errors.push(t('gantt.validationDuplicateWbs', { wbs, rows: rows.join(', ') }));
      }
    }

    return errors;
  }, [t]);

  // ── Show Validation Popup ──────────────────────────────────
  const showValidationPopup = useCallback((title: string, errors: string[]) => {
    setValidationTitle(title);
    setValidationErrors(errors);
  }, []);

  const dismissValidation = useCallback(() => {
    setValidationErrors([]);
    setValidationTitle('');
  }, []);

  // ── Standalone Validate (gate for save) ────────────────────
  const handleValidate = useCallback(() => {
    const errors = validateTasks(draftTasks);
    if (errors.length > 0) {
      showValidationPopup(t('gantt.validationTitle'), errors);
      setValidated(false);
    } else {
      setValidated(true);
      setStatusMsg(t('gantt.validationPassed'));
    }
  }, [draftTasks, t, validateTasks, showValidationPopup]);

  // ── Clear Schedule ────────────────────────────────────────
  const handleClear = useCallback(() => {
    if (draftTasks.length === 0) return;
    if (!window.confirm(t('gantt.clearConfirm'))) return;
    setDraftTasks([]);
    markDirty();
    setClearFlash(true);
    setStatusMsg(t('gantt.clearSuccess'));
    window.setTimeout(() => setClearFlash(false), 900);
  }, [draftTasks.length, t, markDirty]);

  // ── Save ──────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    // Client-side validation first
    const errors = validateTasks(draftTasks);
    if (errors.length > 0) {
      showValidationPopup(t('gantt.validationTitle'), errors);
      return;
    }

    try {
      setSaving(true);
      setStatusMsg(null);
      const result = await batchUpsertGanttTasks(projectGuid, draftTasks);
      const mapped = result.map((r, i) => apiResponseToGanttTask(r, i));
      setTasks(mapped);
      setDraftTasks(mapped);
      setDirty(false);
      setStatusMsg(t('gantt.saveSuccess'));
    } catch (err: any) {
      // Extract detailed error from ApiError.body if available
      const serverMsg = (err instanceof ApiError && err.body?.message)
        ? err.body.message
        : err.message || '';
      showValidationPopup(
        t('gantt.validationServerTitle'),
        [serverMsg || t('gantt.saveError')]
      );
    } finally {
      setSaving(false);
    }
  }, [projectGuid, draftTasks, t, validateTasks, showValidationPopup]);

  // ── Recalculate ───────────────────────────────────────────
  const handleRecalculate = useCallback(async () => {
    try {
      setSaving(true);
      setStatusMsg(null);

      // Auto-save unsaved changes first — recalculate operates on server data
      if (dirty) {
        const errors = validateTasks(draftTasks);
        if (errors.length > 0) {
          showValidationPopup(t('gantt.validationTitle'), errors);
          setSaving(false);
          return;
        }
        await batchUpsertGanttTasks(projectGuid, draftTasks);
      }

      const result = await recalculateGanttSchedule(projectGuid);
      setTasks(result.tasks);
      setDraftTasks(result.tasks);
      setDirty(false);
      setValidated(false);
      if (result.warnings.length > 0) {
        setStatusMsg(t('gantt.recalcWarnings', { count: result.warnings.length }) +
          ': ' + result.warnings.join('; '));
      } else {
        setStatusMsg(t('gantt.recalcSuccess'));
      }
    } catch (err: any) {
      const serverMsg = (err instanceof ApiError && err.body?.message)
        ? err.body.message
        : err.message || '';
      showValidationPopup(
        t('gantt.validationServerTitle'),
        [serverMsg || t('gantt.saveError')]
      );
    } finally {
      setSaving(false);
    }
  }, [projectGuid, dirty, draftTasks, t, validateTasks, showValidationPopup]);

  // ── Scroll to Today ─────────────────────────────────────────
  const scrollToToday = useCallback(() => {
    const chartEl = chartPaneRef.current;
    if (!chartEl) return;
    // Frappe Gantt uses .current-highlight for the today column marker
    const highlight = chartEl.querySelector('.current-highlight') || chartEl.querySelector('.today-highlight');
    if (highlight) {
      highlight.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, []);

  // ── Computed ──────────────────────────────────────────────
  const taskCount = draftTasks.length > 0 ? draftTasks.length : tasks.length;
  const showWarning = taskCount >= WARN_THRESHOLD && taskCount < MAX_TASKS;

  // ── Format date for display ───────────────────────────────
  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    try { return new Date(d + 'T00:00:00').toLocaleDateString(); }
    catch { return d; }
  };

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  if (loading && tasks.length === 0) {
    return <div className={styles.loading}>{t('gantt.title')}…</div>;
  }

  return (
    <div className={styles.ganttPanel}>

      {/* ── In-Panel View (Mode 1) ─────────────────────────── */}
      <div className={styles.toolbar}>
        <h4 className={styles.title}>{t('gantt.title')}</h4>
        <div className={styles.toolbarRight}>
          {/* Zoom toggles */}
          {(['Day', 'Week', 'Month', 'Year'] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              className={`${styles.zoomBtn} ${viewMode === mode ? styles.zoomBtnActive : ''}`}
              onClick={() => setViewMode(mode)}
            >
              {t(`gantt.zoom${mode}`)}
            </button>
          ))}
          {/* DEV-565: Critical path toggle */}
          <button
            className={`${styles.zoomBtn} ${showCriticalPath ? styles.zoomBtnActive : ''}`}
            onClick={() => setShowCriticalPath((prev) => !prev)}
            title={t('gantt.criticalPathToggle')}
          >
            {t('gantt.criticalPath')}
          </button>
          {/* DEV-564: Show Baseline toggle */}
          {baselineSummaries.length > 0 && (
            <>
              <button
                className={`${styles.zoomBtn} ${showBaseline ? styles.zoomBtnActive : ''}`}
                onClick={() => setShowBaseline((prev) => !prev)}
                title={t('gantt.baselineToggle')}
              >
                {t('gantt.showBaseline')}
              </button>
              {showBaseline && baselineSummaries.length > 1 && (
                <select
                  className={styles.baselineSelect}
                  value={selectedBaselineNumber ?? ''}
                  onChange={(e) => setSelectedBaselineNumber(Number(e.target.value))}
                >
                  {baselineSummaries.map((b) => (
                    <option key={b.baselineNumber} value={b.baselineNumber}>
                      {t('gantt.baselineLabel', { number: b.baselineNumber })}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
          <button className={styles.editorBtn} onClick={openEditor}>
            {t('gantt.fullView')}
          </button>
        </div>
      </div>

      {/* Inline Gantt chart (Mode 1 — read-only Frappe Gantt) */}
      {taskCount === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>{t('gantt.noTasks')}</p>
          <p className={styles.emptyHint}>{t('gantt.noTasksHint')}</p>
        </div>
      ) : (
        <div className={styles.inPanelChart}>
          <GanttChart
            tasks={draftTasks}
            viewMode={viewMode}
            onTaskClick={(taskId) => setSelectedTaskId(taskId)}
            selectedTaskId={selectedTaskId}
            colorTheme={colorTheme}
            showCriticalPath={showCriticalPath}
            baselineTasks={baselineTasks}
            showBaseline={showBaseline}
          />
        </div>
      )}

      {showWarning && (
        <p className={styles.warning}>
          {t('gantt.taskLimitWarning', { count: taskCount, max: MAX_TASKS })}
        </p>
      )}

      {error && <p className={styles.errorMsg}>{error}</p>}

      {/* ── Validation Error Popup ─────────────────────────── */}
      {validationErrors.length > 0 && createPortal(
        <div className={styles.validationOverlay} onClick={dismissValidation}>
          <div className={styles.validationPopup} onClick={(e) => e.stopPropagation()}>
            <h4 className={styles.validationTitle}>{validationTitle}</h4>
            <ul className={styles.validationList}>
              {validationErrors.map((msg, i) => (
                <li key={i} className={styles.validationItem}>{msg}</li>
              ))}
            </ul>
            <div className={styles.validationActions}>
              <button className={styles.actionBtn} onClick={dismissValidation}>
                {t('gantt.validationDismiss')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Pop-Out Editor (Mode 2) — portaled to document.body
           to escape the map's stacking context ────────────────── */}
      {editorOpen && createPortal(
        <div className={styles.editorOverlay}>
          <div className={styles.editorModal}>
            {/* ── Single Header: Title + zoom + Save/Close ── */}
            <div className={styles.editorHeader}>
              <h3 className={styles.editorTitle}>
                {t('gantt.editorTitle')}
                {projectName ? ` — ${projectName}` : ''}
              </h3>

              {/* Search bar */}
              <div className={styles.searchBar}>
                <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  ref={searchInputRef}
                  className={styles.searchInput}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      navigateSearch(e.shiftKey ? -1 : 1);
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      clearSearch();
                      searchInputRef.current?.blur();
                    }
                  }}
                  placeholder={t('gantt.searchPlaceholder')}
                />
                {searchTerm && (
                  <>
                    <span className={styles.searchMatchInfo}>
                      {searchMatchIndices.length > 0
                        ? t('gantt.searchMatchCount', {
                            current: activeMatchIdx + 1,
                            total: searchMatchIndices.length,
                          })
                        : t('gantt.searchNoMatches')}
                    </span>
                    <button
                      className={styles.searchNavBtn}
                      onClick={() => navigateSearch(-1)}
                      disabled={searchMatchIndices.length === 0}
                      title={t('gantt.searchPrev')}
                    >
                      &#9650;
                    </button>
                    <button
                      className={styles.searchNavBtn}
                      onClick={() => navigateSearch(1)}
                      disabled={searchMatchIndices.length === 0}
                      title={t('gantt.searchNext')}
                    >
                      &#9660;
                    </button>
                    <button
                      className={styles.searchClearBtn}
                      onClick={clearSearch}
                      title={t('gantt.searchClear')}
                    >
                      &times;
                    </button>
                  </>
                )}
              </div>

              {/* Right cluster: status + zoom + save/close */}
              <div className={styles.headerActions}>
                {statusMsg && (
                  <span className={styles.statusMsg}>{statusMsg}</span>
                )}
                <button
                  className={`${styles.zoomBtn} ${ganttViewMode === 'raci' ? styles.zoomBtnActive : ''}`}
                  onClick={toggleGanttViewMode}
                  title={t('gantt.raciViewToggle')}
                >
                  {t('gantt.raciView')}
                </button>
                <span className={styles.zoomSeparator} />
                <button
                  className={styles.zoomBtn}
                  onClick={scrollToToday}
                >
                  {t('gantt.today')}
                </button>
                <span className={styles.zoomSeparator} />
                {ganttViewMode !== 'raci' && (
                  <>
                    {(['Day', 'Week', 'Month', 'Year'] as ViewMode[]).map((mode) => (
                      <button
                        key={mode}
                        className={`${styles.zoomBtn} ${viewMode === mode ? styles.zoomBtnActive : ''}`}
                        onClick={() => setViewMode(mode)}
                      >
                        {t(`gantt.zoom${mode}`)}
                      </button>
                    ))}
                    {/* DEV-565: Critical path toggle */}
                    <button
                      className={`${styles.zoomBtn} ${showCriticalPath ? styles.zoomBtnActive : ''}`}
                      onClick={() => setShowCriticalPath((prev) => !prev)}
                      title={t('gantt.criticalPathToggle')}
                    >
                      {t('gantt.criticalPath')}
                    </button>
                  </>
                )}
                {/* DEV-564: Baseline controls */}
                {ganttViewMode !== 'raci' && baselineSummaries.length > 0 && (
                  <>
                    <button
                      className={`${styles.zoomBtn} ${showBaseline ? styles.zoomBtnActive : ''}`}
                      onClick={() => setShowBaseline((prev) => !prev)}
                      title={t('gantt.baselineToggle')}
                    >
                      {t('gantt.showBaseline')}
                    </button>
                    {showBaseline && baselineSummaries.length > 1 && (
                      <select
                        className={styles.baselineSelect}
                        value={selectedBaselineNumber ?? ''}
                        onChange={(e) => setSelectedBaselineNumber(Number(e.target.value))}
                      >
                        {baselineSummaries.map((b) => (
                          <option key={b.baselineNumber} value={b.baselineNumber}>
                            {t('gantt.baselineLabel', { number: b.baselineNumber })}
                          </option>
                        ))}
                      </select>
                    )}
                  </>
                )}
                {ganttViewMode !== 'raci' && canEdit && (
                  <button
                    className={styles.zoomBtn}
                    onClick={handleLockBaseline}
                    disabled={saving || tasks.length === 0}
                    title={t('gantt.lockBaselineTitle')}
                  >
                    {t('gantt.lockBaseline')}
                  </button>
                )}
                <span className={styles.zoomSeparator} />
                <button className={styles.closeBtn} onClick={closeEditor}>
                  {t('gantt.close')}
                </button>
              </div>
            </div>

            {/* Editor content — resizable grid + chart side-by-side */}
            <PanelGroup key={ganttViewMode} orientation="horizontal" className={styles.editorContent}>
              {/* Grid (left side) */}
              <Panel defaultSize={ganttViewMode === 'raci' ? 100 : 55} minSize={25}>
              <div ref={gridPaneRef} className={styles.gridPane} onPaste={canEdit ? handlePaste : undefined}>
                <table className={styles.gridTable} style={{ width: (() => {
                  let total = 0;
                  if (canEdit) total += 72;                                          // row actions (drag + insert + delete)
                  if (isColVisible('taskId')) total += getColWidth('taskId');
                  if (isColVisible('wbs')) total += getColWidth('wbs');
                  total += getColWidth('taskName');                                   // always visible
                  if (isColVisible('summaryLevel')) total += getColWidth('summaryLevel');
                  if (isColVisible('duration')) total += getColWidth('duration');
                  if (isColVisible('start')) total += getColWidth('start');
                  if (isColVisible('end')) total += getColWidth('end');
                  if (isColVisible('predecessors')) total += getColWidth('predecessors');
                  if (isColVisible('pctComplete')) total += getColWidth('pctComplete');
                  if (isColVisible('float')) total += getColWidth('float');
                  if (isColVisible('pinned')) total += getColWidth('pinned');
                  if (isColVisible('roles')) total += getColWidth('roles');
                  return total;
                })() }}>
                  <colgroup>
                    {canEdit && <col style={{ width: 72 }} />}    {/* Row actions: drag + insert + delete */}
                    {isColVisible('taskId') && <col style={{ width: getColWidth('taskId') }} />}
                    {isColVisible('wbs') && <col style={{ width: getColWidth('wbs') }} />}
                    <col style={{ width: getColWidth('taskName') }} />  {/* Task Name — always visible, explicit width */}
                    {isColVisible('summaryLevel') && <col style={{ width: getColWidth('summaryLevel') }} />}
                    {isColVisible('duration') && <col style={{ width: getColWidth('duration') }} />}
                    {isColVisible('start') && <col style={{ width: getColWidth('start') }} />}
                    {isColVisible('end') && <col style={{ width: getColWidth('end') }} />}
                    {isColVisible('predecessors') && <col style={{ width: getColWidth('predecessors') }} />}
                    {isColVisible('pctComplete') && <col style={{ width: getColWidth('pctComplete') }} />}
                    {isColVisible('float') && <col style={{ width: getColWidth('float') }} />}
                    {isColVisible('pinned') && <col style={{ width: getColWidth('pinned') }} />}
                    {isColVisible('roles') && <col style={{ width: getColWidth('roles') }} />}
                    {/* Delete column removed — trash icon now in row actions cell */}
                  </colgroup>
                  <thead>
                    {/* Grid toolbar — indent/outdent/recalculate above column names */}
                    {canEdit && (
                      <tr className={styles.gridToolbarRow}>
                        <th colSpan={
                          (canEdit ? 1 : 0)                                       /* row actions (drag+insert+delete) */
                          + 1                                                     /* Task Name (always) */
                          + COLUMN_DEFS.filter((c) => isColVisible(c.key)).length  /* toggleable cols */
                        }>
                          <div className={styles.gridToolbar}>
                            <button
                              className={styles.actionBtn}
                              onClick={() => outdentRows(selectedIndices)}
                              disabled={selectedIndices.size === 0 || (() => {
                                // Disable if all selected rows are already at level 0
                                for (const idx of selectedIndices) {
                                  if ((draftTasks[idx]?.indentLevel ?? 0) > 0) return false;
                                }
                                return true;
                              })()}
                              title={t('gantt.outdent')}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M2 3h12M6 7h8M6 11h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M4.5 9L2 7l2.5-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                              </svg>
                            </button>
                            <button
                              className={styles.actionBtn}
                              onClick={() => indentRows(selectedIndices)}
                              disabled={selectedIndices.size === 0 || (() => {
                                // Disable if no selected row can be indented
                                for (const idx of selectedIndices) {
                                  if (idx === 0) continue; // first row can't indent
                                  const cur = draftTasks[idx]?.indentLevel ?? 0;
                                  const above = draftTasks[idx - 1]?.indentLevel ?? 0;
                                  if (cur < above + 1) return false; // at least one can indent
                                }
                                return true;
                              })()}
                              title={t('gantt.indent')}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M2 3h12M6 7h8M6 11h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M2 5l2.5 2L2 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                              </svg>
                            </button>
                            <button
                              className={`${styles.actionBtn} ${styles.saveBtn}`}
                              onClick={handleRecalculate}
                              disabled={saving}
                            >
                              {saving ? '…' : t('gantt.recalculateAndSave')}
                            </button>
                            <span className={styles.zoomSeparator} />
                            <button
                              className={`${styles.actionBtn} ${validated ? styles.validateBtnPassed : styles.validateBtn}`}
                              onClick={handleValidate}
                              disabled={saving || draftTasks.length === 0}
                            >
                              {validated ? `\u2713 ${t('gantt.validated')}` : t('gantt.validate')}
                            </button>
                            <button
                              className={`${styles.actionBtn} ${styles.clearBtn} ${clearFlash ? styles.clearFlash : ''}`}
                              onClick={handleClear}
                              disabled={saving || draftTasks.length === 0}
                            >
                              {t('gantt.clear')}
                            </button>
                            {/* Role assignment — selection action bar */}
                            {selectedIndices.size > 0 && roleSlots.length > 0 && (
                              <>
                                <span className={styles.zoomSeparator} />
                                <span className={styles.selectionCount}>
                                  {t('gantt.bulkAssignCount', { count: selectedIndices.size })}
                                </span>
                                <button
                                  className={`${styles.actionBtn} ${styles.assignRoleBtn}`}
                                  onClick={() => setRoleTrayOpen(true)}
                                >
                                  {t('gantt.assignRole')}
                                </button>
                                <button
                                  className={styles.actionBtn}
                                  onClick={() => setSelectedIndices(new Set())}
                                >
                                  {t('gantt.clearSelection')}
                                </button>
                              </>
                            )}
                            {/* DEV-567: Column visibility dropdown */}
                            <span className={styles.zoomSeparator} />
                            <div className={styles.columnsDropdownWrapper} ref={columnsDropdownRef}>
                              <button
                                className={styles.actionBtn}
                                onClick={() => setColumnsDropdownOpen((prev) => !prev)}
                                title={t('gantt.columnsToggle')}
                              >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ marginInlineEnd: 4, verticalAlign: 'middle' }}>
                                  <rect x="1" y="1" width="4" height="12" rx="1" stroke="currentColor" strokeWidth="1.3" />
                                  <rect x="7" y="1" width="6" height="12" rx="1" stroke="currentColor" strokeWidth="1.3" />
                                </svg>
                                {t('gantt.columnsToggle')}
                              </button>
                              {columnsDropdownOpen && (
                                <div className={styles.columnsDropdown}>
                                  {COLUMN_DEFS.map((col) => {
                                    // Hide feature-gated columns from the dropdown when the gate is off
                                    if (col.featureGate === 'criticalPath' && !showCriticalPath) return null;
                                    return (
                                      <label key={col.key} className={styles.columnsDropdownItem}>
                                        <input
                                          type="checkbox"
                                          checked={visibleColumns.has(col.key)}
                                          onChange={() => visibleColumns.has(col.key) ? hideColumn(col.key) : showColumn(col.key)}
                                        />
                                        {t(`gantt.${col.labelKey}`)}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </th>
                      </tr>
                    )}
                    <tr>
                      {canEdit && <th className={styles.colRowActions}></th>}
                      {isColVisible('taskId') && (
                        <th className={styles.colTaskId}>
                          <div className={styles.thInner}>
                            {t('gantt.colTaskId')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'taskId')}
                              onDoubleClick={() => handleResizeDoubleClick('taskId')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('wbs') && (
                        <th className={styles.colWbs}>
                          <div className={styles.thInner}>
                            {t('gantt.colWbs')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'wbs')}
                              onDoubleClick={() => handleResizeDoubleClick('wbs')}
                            />
                          </div>
                        </th>
                      )}
                      <th className={styles.colTask}>
                        <div className={styles.thInner}>
                          {t('gantt.colTask')}
                          <span
                            className={styles.colResizeHandle}
                            onPointerDown={(e) => handleResizePointerDown(e, 'taskName')}
                            onDoubleClick={() => handleResizeDoubleClick('taskName')}
                          />
                        </div>
                      </th>
                      {isColVisible('summaryLevel') && (
                        <th className={styles.colSummaryLevel}>
                          <div className={styles.thInner}>
                            {t('gantt.colSummaryLevel')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'summaryLevel')}
                              onDoubleClick={() => handleResizeDoubleClick('summaryLevel')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('duration') && (
                        <th className={styles.colDur}>
                          <div className={styles.thInner}>
                            {t('gantt.colDuration')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'duration')}
                              onDoubleClick={() => handleResizeDoubleClick('duration')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('start') && (
                        <th className={styles.colDate}>
                          <div className={styles.thInner}>
                            {t('gantt.colStart')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'start')}
                              onDoubleClick={() => handleResizeDoubleClick('start')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('end') && (
                        <th className={styles.colDate}>
                          <div className={styles.thInner}>
                            {t('gantt.colEnd')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'end')}
                              onDoubleClick={() => handleResizeDoubleClick('end')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('predecessors') && (
                        <th className={styles.colPred}>
                          <div className={styles.thInner}>
                            {t('gantt.colPredecessors')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'predecessors')}
                              onDoubleClick={() => handleResizeDoubleClick('predecessors')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('pctComplete') && (
                        <th className={styles.colPct}>
                          <div className={styles.thInner}>
                            {t('gantt.colComplete')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'pctComplete')}
                              onDoubleClick={() => handleResizeDoubleClick('pctComplete')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('float') && (
                        <th className={styles.colFloat}>
                          <div className={styles.thInner}>
                            {t('gantt.colFloat')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'float')}
                              onDoubleClick={() => handleResizeDoubleClick('float')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('pinned') && (
                        <th className={styles.colPin}>
                          <div className={styles.thInner}>
                            {t('gantt.colPinned')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'pinned')}
                              onDoubleClick={() => handleResizeDoubleClick('pinned')}
                            />
                          </div>
                        </th>
                      )}
                      {isColVisible('roles') && (
                        <th className={styles.colRoles}>
                          <div className={styles.thInner}>
                            {t('gantt.colRoles')}
                            <span
                              className={styles.colResizeHandle}
                              onPointerDown={(e) => handleResizePointerDown(e, 'roles')}
                              onDoubleClick={() => handleResizeDoubleClick('roles')}
                            />
                          </div>
                        </th>
                      )}
                      {/* Delete column removed — trash icon now in row actions cell */}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Apply rollup so summary rows show computed % complete
                      const displayTasks = rollupPercentComplete(draftTasks);
                      return visibleIndices.map((idx) => {
                        const task = displayTasks[idx];
                        const isCollapsed = task.isSummary && collapsedIds.has(task.taskId);
                        return (
                      <tr
                        key={idx}
                        className={[
                          styles.gridRow,
                          task.isSummary ? styles.summaryRow : '',
                          selectedIndices.has(idx) ? styles.gridRowSelected : '',
                          canEdit && dragIndex === idx ? styles.gridRowDragging : '',
                          canEdit && dragOverIndex === idx ? styles.gridRowDropTarget : '',
                          searchMatchSet.has(idx) ? styles.searchMatch : '',
                          idx === currentMatchDraftIdx ? styles.searchMatchActive : '',
                        ].filter(Boolean).join(' ')}
                        onClick={(e) => handleRowClick(idx, e)}
                        onDragOver={canEdit ? (e) => handleDragOver(e, idx) : undefined}
                        onDragLeave={canEdit ? handleDragLeave : undefined}
                        onDrop={canEdit ? (e) => handleDrop(e, idx) : undefined}
                        onDragEnd={canEdit ? handleDragEnd : undefined}
                      >
                        {canEdit && (
                          <td className={styles.rowActionsCell}>
                            <span
                              className={styles.dragHandle}
                              title="Drag to reorder"
                              draggable
                              onDragStart={(e) => handleDragStart(e, idx)}
                            >⠿</span>
                            <button
                              className={styles.insertBtn}
                              onClick={(e) => { e.stopPropagation(); insertAfter(idx); }}
                              title={t('gantt.insertAfter')}
                              disabled={draftTasks.length >= MAX_TASKS}
                            >
                              +
                            </button>
                            <button
                              className={styles.deleteBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                let childCount = 0;
                                if (task.isSummary) {
                                  for (let ci = idx + 1; ci < draftTasks.length; ci++) {
                                    if (draftTasks[ci].indentLevel <= task.indentLevel) break;
                                    childCount++;
                                  }
                                }
                                const msg = childCount > 0
                                  ? t('gantt.deleteSummaryConfirm', { name: task.taskName || `#${task.taskId}`, count: childCount })
                                  : t('gantt.deleteRowConfirm', { name: task.taskName || `#${task.taskId}` });
                                if (!window.confirm(msg)) return;
                                deleteRow(idx);
                              }}
                              title={t('gantt.deleteConfirm')}
                            >
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M5.5 1h5M1 3.5h14M3.5 3.5V14a1 1 0 001 1h7a1 1 0 001-1V3.5M6.5 6.5v5M9.5 6.5v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </td>
                        )}
                        {/* TaskID — auto-generated, read-only */}
                        {isColVisible('taskId') && (
                        <td className={styles.taskIdCell}>
                          <span className={styles.readOnly}>{task.taskId}</span>
                        </td>
                        )}
                        {/* WBS — optional, user-editable */}
                        {isColVisible('wbs') && (
                        <td>
                          {canEdit ? (
                            <input
                              className={styles.cellInput}
                              value={task.wbs}
                              onChange={(e) => updateDraftField(idx, 'wbs', e.target.value)}
                              placeholder=""
                            />
                          ) : (
                            <span className={styles.readOnly}>
                              {task.wbs || '—'}
                            </span>
                          )}
                        </td>
                        )}
                        <td className={styles.taskCell}>
                          {/* Collapse/expand chevron for summary rows */}
                          {task.isSummary && (
                            <button
                              className={styles.collapseBtn}
                              onClick={(e) => { e.stopPropagation(); isCollapsed ? expandTask(task.taskId) : collapseTask(task.taskId); }}
                              title={isCollapsed ? t('gantt.expand') : t('gantt.collapse')}
                              style={{ marginInlineStart: `${task.indentLevel * 16}px` }}
                            >
                              {isCollapsed ? '\u25B6' : '\u25BC'}
                            </button>
                          )}
                          {/* Diamond icon for milestone rows */}
                          {task.isMilestone && !task.isSummary && (
                            <span
                              className={styles.milestoneIcon}
                              style={{ marginInlineStart: `${task.indentLevel * 16}px` }}
                              title={t('gantt.milestone')}
                            >
                              &#9670;
                            </span>
                          )}
                          {canEdit ? (
                            <input
                              className={`${styles.cellInput} ${task.isSummary ? styles.summaryText : ''}`}
                              value={task.taskName}
                              onChange={(e) => updateDraftField(idx, 'taskName', e.target.value)}
                              style={{
                                width: '100%',
                                paddingInlineStart: `${(task.isSummary ? 18 : task.isMilestone ? 20 : 6) + task.indentLevel * 16}px`,
                              }}
                            />
                          ) : (
                            <span
                              className={`${styles.readOnly} ${task.isSummary ? styles.summaryText : ''}`}
                              style={{ paddingInlineStart: `${(task.isSummary ? 18 : task.isMilestone ? 20 : 0) + task.indentLevel * 16}px` }}
                            >
                              {task.taskName}
                            </span>
                          )}
                        </td>
                        {isColVisible('summaryLevel') && (
                        <td className={styles.summaryLevelCell}>
                          {canEdit ? (
                            <select
                              className={styles.cellSelect}
                              value={task.summaryLevel || ''}
                              onChange={(e) => updateDraftField(idx, 'summaryLevel', e.target.value || null)}
                            >
                              <option value="">{'\u2014'}</option>
                              <option value="phase">{t('gantt.summaryLevelPhase')}</option>
                              <option value="highlight">{t('gantt.summaryLevelHighlight')}</option>
                            </select>
                          ) : (
                            <span className={styles.readOnly}>
                              {task.summaryLevel === 'phase' ? t('gantt.summaryLevelPhase')
                                : task.summaryLevel === 'highlight' ? t('gantt.summaryLevelHighlight')
                                : '\u2014'}
                            </span>
                          )}
                        </td>
                        )}
                        {isColVisible('duration') && (
                        <td>
                          {canEdit && !task.isSummary ? (
                            <input
                              className={styles.cellInput}
                              type="number"
                              value={task.isMilestone ? 0 : (task.durationDays ?? '')}
                              onChange={(e) =>
                                updateDraftField(idx, 'durationDays', e.target.value ? parseInt(e.target.value) : null)
                              }
                              style={{ width: 55 }}
                              min={0}
                              title={task.isMilestone ? t('gantt.milestoneZeroDuration') : undefined}
                            />
                          ) : (
                            <span className={styles.readOnly}>{task.isMilestone ? '0' : (task.durationDays ?? '—')}</span>
                          )}
                        </td>
                        )}
                        {isColVisible('start') && (
                        <td className={styles.dateCell}>
                          {canEdit && !task.isSummary ? (
                            <input
                              className={styles.cellInput}
                              type="date"
                              value={task.startDate ?? ''}
                              onChange={(e) => updateDraftField(idx, 'startDate', e.target.value || null)}
                            />
                          ) : (
                            <span className={styles.readOnly}>{fmtDate(task.startDate)}</span>
                          )}
                        </td>
                        )}
                        {isColVisible('end') && (
                        <td className={styles.dateCell}>
                          {canEdit && !task.isSummary ? (
                            <input
                              className={styles.cellInput}
                              type="date"
                              value={task.endDate ?? ''}
                              onChange={(e) => updateDraftField(idx, 'endDate', e.target.value || null)}
                            />
                          ) : (
                            <span className={styles.readOnly}>{fmtDate(task.endDate)}</span>
                          )}
                        </td>
                        )}
                        {isColVisible('predecessors') && (
                        <td>
                          {canEdit ? (
                            <input
                              className={styles.cellInput}
                              value={task.predecessors}
                              onChange={(e) => updateDraftField(idx, 'predecessors', e.target.value)}
                              style={{ width: 100 }}
                              placeholder={t('gantt.predecessorHint')}
                              title={t('gantt.predecessorHint')}
                            />
                          ) : (
                            <span className={styles.readOnly}>{task.predecessors || '—'}</span>
                          )}
                        </td>
                        )}
                        {isColVisible('pctComplete') && (
                        <td>
                          {canEdit && !task.isSummary ? (
                            <input
                              className={styles.cellInput}
                              type="number"
                              value={task.percentComplete}
                              onChange={(e) =>
                                updateDraftField(idx, 'percentComplete', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))
                              }
                              style={{ width: 50 }}
                              min={0}
                              max={100}
                            />
                          ) : (
                            <span className={styles.readOnly}>{task.percentComplete}%</span>
                          )}
                        </td>
                        )}
                        {/* DEV-565: Float column (read-only, shown when critical path is on) */}
                        {isColVisible('float') && (
                          <td className={styles.floatCell}>
                            <span
                              className={`${styles.readOnly} ${task.isCritical ? styles.criticalFloat : ''}`}
                              title={task.isCritical ? t('gantt.criticalTask') : ''}
                            >
                              {task.totalFloat != null ? task.totalFloat : '—'}
                            </span>
                          </td>
                        )}
                        {isColVisible('pinned') && (
                        <td className={styles.pinCell}>
                          <input
                            type="checkbox"
                            checked={task.isPinned}
                            disabled={!canEdit || task.isSummary}
                            onChange={canEdit ? (e) => updateDraftField(idx, 'isPinned', e.target.checked) : undefined}
                          />
                        </td>
                        )}
                        {isColVisible('roles') && (
                        <td
                          className={`${styles.rolesCell} ${canEdit ? styles.rolesCellClickable : ''}`}
                          onClick={canEdit && roleSlots.length > 0 ? (e) => {
                            // Don't open the tray if user clicked on a role badge
                            // (the badge has its own click handler for the RACI popover)
                            const target = e.target as HTMLElement;
                            if (target.closest('[data-role-badge]')) return;
                            e.stopPropagation();
                            // Select just this task and open the tray
                            setSelectedIndices(new Set([idx]));
                            setRoleTrayOpen(true);
                          } : undefined}
                          title={canEdit ? t('gantt.assignRole') : undefined}
                        >
                          <RoleBadgeGroup
                            assignments={taskRoleMap.get(task.id.toUpperCase()) || []}
                            canEdit={canEdit}
                            maxVisible={2}
                            onRemove={handleRemoveRoleAssignment}
                            onChangeType={handleChangeRoleType}
                          />
                        </td>
                        )}
                        {/* Delete button is now in rowActionsCell alongside + */}
                      </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>

                {draftTasks.length === 0 && (
                  <div className={styles.gridEmpty}>
                    <p>{t('gantt.noTasks')}</p>
                    {canEdit && (
                      <>
                        <p className={styles.emptyHint}>{t('gantt.pasteTip')}</p>
                        <button className={styles.actionBtn} onClick={addRow} style={{ marginTop: 8 }}>
                          + {t('gantt.addTask')}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {draftTasks.length >= MAX_TASKS && (
                  <p className={styles.warning}>
                    {t('gantt.taskLimitReached', { max: MAX_TASKS })}
                  </p>
                )}
              </div>
              </Panel>

              {ganttViewMode !== 'raci' && (
                <>
                  <PanelSeparator className={styles.panelSeparator} />

                  {/* Chart (right side) — Frappe Gantt */}
                  <Panel defaultSize={45} minSize={20}>
                  <div ref={chartPaneRef} className={styles.chartPane}>
                    <GanttChart
                      tasks={visibleDraftTasks}
                      viewMode={viewMode}
                      onTaskClick={(taskId) => setSelectedTaskId(taskId)}
                      selectedTaskId={selectedTaskId}
                      colorTheme={colorTheme}
                      showCriticalPath={showCriticalPath}
                      baselineTasks={baselineTasks}
                      showBaseline={showBaseline}
                    />
                  </div>
                  </Panel>
                </>
              )}
            </PanelGroup>

            {/* Role assignment tray (slides in from the right) */}
            {roleTrayOpen && canEdit && (
              <GanttRoleAssignmentTray
                roleSlots={roleSlots}
                selectedIndices={selectedIndices}
                tasks={draftTasks}
                assignmentMap={taskRoleMap}
                onAssignRole={handleBulkAssignRole}
                onClose={() => setRoleTrayOpen(false)}
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
